import { useState, useMemo, useEffect } from 'react'
import { DateTime } from 'luxon'
import { useNavigate } from 'react-router-dom'
import { light, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { selectInsightSummary } from '@/reducers/insights.slice'
import { getIsFetching } from '@/reducers/fetch'
import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  updateFinancialProfile,
  UPDATE_FINANCIAL_PROFILE_KEY,
} from '@/actions/financialProfileActions'

import { useReselector, useToggle } from '@/utils/sharedHooks'
import {
  dollarsToCents,
  centsToDollars,
  formatCurrencyFromCentsAndRound,
} from '@/utils/currencyHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

import { useAnalyticsTrack, useAnalyticsView } from '@/features/amplitude'
import { TextColors, TypographyStyles } from '@/styles'
import {
  Button,
  Card,
  CardColors,
  Progress,
  Input,
  Text,
  Icon,
  Badge,
  Label,
} from '@/ui'

//TODO: Migrate to V2 skipcq: JS-0099
import {
  INSIGHTS_TYPE,
  getInsightsMonthYear,
} from '@/componentsV1/Finances/shared/utils'
import {
  FETCH_INSIGHTS_SUMMARY_KEY,
  fetchInsightsSummary,
} from '@/componentsV1/Finances/shared/financeActions'

export const IncomeGoalCard = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()
  const pageView = useAnalyticsView()
  const financialProfile = useReselector(getFinancialProfile)
  const updateLoading = useReselector(
    getIsFetching,
    UPDATE_FINANCIAL_PROFILE_KEY(financialProfile?.id)
  )

  const [annualIncome, setAnnualIncome] = useState(
    financialProfile?.annualIncomeGoalInCents
      ? centsToDollars(financialProfile.annualIncomeGoalInCents).toString()
      : ''
  )
  const [editing, toggleEditing] = useToggle()

  const { month, year } = getInsightsMonthYear(DateTime.now())

  useEffect(() => {
    pageView('income goal progress card')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchInsightsSummary(INSIGHTS_TYPE.INCOME, month, year))
  }, [dispatch, month, year])

  const summaryDataResponse = useReselector(
    selectInsightSummary,
    FETCH_INSIGHTS_SUMMARY_KEY(INSIGHTS_TYPE.INCOME, month, year)
  )

  const handleTrack = async () => {
    await updateFinancialProfile(financialProfile?.id, {
      annualIncomeGoalInCents: dollarsToCents(annualIncome),
    })(dispatch)
    if (editing) {
      toggleEditing()
    }
  }

  const displayState = useMemo(() => {
    if (financialProfile?.annualIncomeGoalInCents === null) {
      return 'empty'
    } else if (editing) {
      return 'editing'
    } else {
      return 'progress'
    }
  }, [financialProfile, editing])

  const incomeYearToDate = useMemo(() => {
    if (!summaryDataResponse?.[0]) return 0
    if (summaryDataResponse[0].ytd_total_in_cents === null) return 0
    return summaryDataResponse[0].ytd_total_in_cents
  }, [summaryDataResponse])

  const incomeGoalProgress = useMemo(() => {
    if (!financialProfile?.annualIncomeGoalInCents) return 0
    return Math.round(
      (incomeYearToDate / financialProfile.annualIncomeGoalInCents) * 100
    )
  }, [incomeYearToDate, financialProfile])

  return (
    <Card color={CardColors.LEVEL_1}>
      {displayState === 'empty' && (
        <div className="space-y-4">
          <div>
            <Badge
              color="orange"
              variant="secondary"
              className="mb-3 inline-flex items-center"
            >
              <Icon icon={solid('sparkle')} className="mr-2" />
              NEW
            </Badge>
            <Text variant={TypographyStyles.H2}>
              Track your annual income goals
            </Text>
          </div>

          <Text variant={TypographyStyles.BODY_SM}>
            Enter your target annual income for 2025 to track your progress
            here.
          </Text>

          <div>
            <Label>2025 Target Annual Income</Label>
            <Input
              placeholder="$50,000"
              value={annualIncome}
              onChange={(e) => setAnnualIncome(e.target.value)}
              // componentType="currency"
            />
          </div>

          <div>
            <Button
              variant="secondary"
              onClick={() => {
                handleTrack()
                track('clicked track button', { button: 'track income' })
              }}
              loading={updateLoading}
            >
              Track <Icon icon={light('arrow-right')} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {displayState === 'progress' && (
        <div className="space-y-4">
          <Text variant={TypographyStyles.H2}>Annual Income Goal</Text>

          <div className="flex flex-row items-center gap-2">
            <Text variant={TypographyStyles.H2} color={TextColors.GREEN}>
              {incomeGoalProgress}%
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              towards your 2025 target annual income
            </Text>
          </div>

          <div className="space-y-2">
            <Text variant={TypographyStyles.BODY_SM}>
              {formatCurrencyFromCentsAndRound(incomeYearToDate, {
                hideDecimalsIfZero: true,
              })}{' '}
              /{' '}
              {formatCurrencyFromCentsAndRound(
                financialProfile?.annualIncomeGoalInCents,
                { hideDecimalsIfZero: true }
              )}{' '}
              <Button
                onClick={() => {
                  toggleEditing()
                  track('clicked edit button', { button: 'edit income' })
                }}
                variant="link"
                size="sm"
              >
                (edit)
              </Button>
            </Text>
            <Progress value={incomeGoalProgress} size="large" />

            <Button
              onClick={() => {
                navigate('/insights/income')
                track('clicked view income button', { button: 'view income' })
              }}
            >
              View Income <Icon icon={light('arrow-right')} className="ml-2" />
            </Button>
          </div>
        </div>
      )}

      {displayState === 'editing' && (
        <div className="space-y-4">
          <div>
            <Text variant={TypographyStyles.H2}>Annual Income Goal</Text>
          </div>

          <div>
            <Text variant={TypographyStyles.H3}>2025 Target Annual Income</Text>
            <Text variant={TypographyStyles.BODY_XS} className="mt-1 mb-1">
              Enter a new annual income goal. We&apos;ll update your progress to
              reflect the new target.
            </Text>
            <Input
              placeholder="$50,000"
              value={annualIncome}
              onChange={(e) => setAnnualIncome(e.target.value)}
              // TODO: Add currency input skipcq: JS-0099
              // componentType="currency"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            <Button
              onClick={() => {
                handleTrack()
                track('clicked update goal button', { button: 'update goal' })
              }}
              loading={updateLoading}
              size="sm"
            >
              Update Goal
            </Button>
            <Button
              onClick={toggleEditing}
              loading={updateLoading}
              size="sm"
              variant="link"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}
